import { useGetBetsSports, useGetReport } from "@/api/queries/logins";
import type { LoginItemType } from "@/api/queries/logins/types";
import { CreatableMultiSelect } from "@/components/creatable-multi-select";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Message } from "@/components/ui/message";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { exportCSV, exportEXCEL } from "@/lib/export";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

interface ReportDialogProps {
  loginItem: LoginItemType;
}

const schema = z.object({
  koTimeDateRange: z
    .object(
      {
        from: z.date(),
        to: z.date(),
      },
      { message: "Please select date range" },
    )
    .optional(),

  sport: z.array(z.string()).optional(),
  betResult: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof schema>;

const exportIcons = {
  csv: <FileText className="h-4 w-4" />,
  excel: <FileSpreadsheet className="h-4 w-4" />,
};

export const exportLabels: Record<string, string> = {
  csv: "CSV",
  excel: "Excel (.xlsx)",
};

export const ExportDialog = ({ loginItem }: ReportDialogProps) => {
  const [open, setOpen] = useState(false);
  const [exportType, setExportType] = useState<string | null>("excel");
  const { mutateAsync, isPending } = useGetReport();
  const { data: sports } = useGetBetsSports();

  const sportOptions = useMemo(() => {
    return sports?.map((sport) => ({ label: sport, value: sport })) ?? [];
  }, [sports]);

  const betResultOptions = useMemo(() => {
    return [
      { label: "Push", value: "Push" },
      { label: "Abnormal Void", value: "Abnormal Void" },
      { label: "Half Win", value: "Half Win" },
      { label: "Win", value: "Win" },
      { label: "Canceled", value: "Canceled" },
      { label: "Lose", value: "Lose" },
      { label: "Half Lose", value: "Half Lose" },
    ];
  }, []);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      sport: sportOptions.map((option) => option.value),
      betResult: betResultOptions.map((option) => option.value),
    },
  });

  const onSubmit = async ({ koTimeDateRange, ...values }: FormData) => {
    if (!exportType) {
      return;
    }

    mutateAsync({
      loginName: loginItem.username,
      ...(koTimeDateRange && {
        fromKoTime: koTimeDateRange.from.toISOString(),
        toKoTime: koTimeDateRange.to.toISOString(),
      }),
      ...values,
    })
      .then((res) => {
        const filename = `report-${loginItem.username}-${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}`;
        switch (exportType) {
          case "csv":
            exportCSV(filename, res);
            break;
          case "excel":
            exportEXCEL(filename, res);
            break;
        }

        toast.success(`Report exported successfully as ${exportType.toUpperCase()}.`);
      })
      .catch(() => {
        toast.error("Failed to export the report. Please try again later.");
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button data-testid="export-bets-trigger-btn" className="w-full justify-start gap-2">
          <Download className="size-4" />
          Export bets
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export report</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Ko Time Date Range</Label>
            <Controller
              name="koTimeDateRange"
              control={control}
              render={({ field }) => (
                <DatePickerWithRange
                  onSelect={(range) => {
                    field.onChange({ target: { value: range } });
                  }}
                  date={field.value}
                  label=""
                />
              )}
            />
            {errors.koTimeDateRange && (
              <Message variant="error">{errors.koTimeDateRange.message}</Message>
            )}
          </div>

          <div className="space-y-2">
            <Label>Sport</Label>

            <Controller
              name="sport"
              control={control}
              render={({ field }) => (
                <CreatableMultiSelect
                  options={sportOptions}
                  value={field.value}
                  onChange={(value) => field.onChange({ target: { value } })}
                />
              )}
            />
            {errors.sport && <Message variant="error">{errors.sport.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Bet Result</Label>

            <Controller
              name="betResult"
              control={control}
              render={({ field }) => (
                <CreatableMultiSelect
                  options={betResultOptions}
                  value={field.value}
                  onChange={(value) => field.onChange({ target: { value } })}
                />
              )}
            />
            {errors.betResult && <Message variant="error">{errors.betResult.message}</Message>}
          </div>

          <Select value={exportType} onValueChange={setExportType} disabled={isPending}>
            <SelectTrigger className="w-[180px]">
              {exportType ? (
                <div className="flex items-center gap-2">
                  {exportIcons["csv"]}
                  <span>{exportLabels[exportType]}</span>
                </div>
              ) : (
                <SelectValue placeholder="Export format" />
              )}
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="csv">
                <div className="flex items-center gap-2">
                  {exportIcons["csv"]}
                  <span>{exportLabels.csv}</span>
                </div>
              </SelectItem>

              <SelectItem value="excel">
                <div className="flex items-center gap-2">
                  {exportIcons["excel"]}
                  <span>{exportLabels.excel}</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <Button className="w-full" type="submit" disabled={isPending}>
            {isPending ? "Exporting..." : "Export"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
